import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertOctagon, Trash2, Check, Flag, ShieldCheck } from "lucide-react";

const REASON_LABEL = {
  adult: "🔞 18+",
  spam: "Spam",
  harassment: "Harassment",
  other: "Other",
};

const TARGET_LINK = {
  proposal: (id) => `/proposals/${id}`,
  thread: () => `/rooms`,
  reply: () => `/rooms`,
  video: () => `/learn`,
};

export default function AdminReports() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [tab, setTab] = useState("open");
  const [isAdmin, setIsAdmin] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/reports", { params: { status: tab === "all" ? undefined : tab } });
      setItems(data.items);
      setOpenCount(data.open_count);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load reports");
    }
  }, [tab]);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/whoami").then((r) => setIsAdmin(!!r.data?.is_admin)).catch(() => setIsAdmin(false));
  }, [user]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const dismiss = async (id) => {
    try { await api.post("/admin/reports/dismiss", { report_id: id }); toast.success("Dismissed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const action = async (id) => {
    if (!window.confirm("Delete the reported content? This cannot be undone.")) return;
    try { await api.post("/admin/reports/action", { report_id: id }); toast.success("Content deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (!user) return <Navigate to="/login" />;
  if (isAdmin === null) return <div className="mx-auto max-w-6xl px-6 py-24 text-muted-foreground">Checking access…</div>;
  if (!isAdmin) return <div className="mx-auto max-w-6xl px-6 py-24 text-muted-foreground">Admins only.</div>;

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs text-primary flex items-center gap-2">// admin · reports <ShieldCheck className="h-3 w-3" /></div>
          <h1 className="mt-2 text-5xl font-black tracking-tighter">Reports inbox.</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">User-flagged proposals, threads, replies and videos. 🔞 Adult flags trigger an alert.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="border border-border px-3 py-1.5 text-xs uppercase tracking-widest">
            Open: <span className="text-primary font-bold" data-testid="admin-reports-open-count">{openCount}</span>
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-1">
        {["open", "actioned", "dismissed", "all"].map((t) => (
          <button
            key={t}
            data-testid={`reports-tab-${t}`}
            onClick={() => setTab(t)}
            className={`border px-3 py-1.5 text-xs uppercase tracking-widest ${tab === t ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center text-muted-foreground">
          <AlertOctagon className="mx-auto h-8 w-8 text-primary/60" />
          <div className="mt-3">No {tab === "all" ? "" : tab} reports.</div>
        </div>
      ) : (
        <div className="border border-border">
          {items.map((r, i) => {
            const isAdult = r.reason === "adult";
            const link = TARGET_LINK[r.target_type]?.(r.target_id);
            return (
              <div key={r.id} data-testid={`report-row-${r.id}`} className={`p-4 ${i > 0 ? "border-t border-border" : ""} ${isAdult ? "bg-primary/5" : ""}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <Flag className={`h-3 w-3 ${isAdult ? "text-primary" : "text-muted-foreground"}`} />
                      <span className={`uppercase tracking-widest ${isAdult ? "text-primary font-bold" : "text-muted-foreground"}`}>{REASON_LABEL[r.reason] || r.reason}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="uppercase tracking-widest text-muted-foreground">{r.target_type}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">reported by {r.reporter_name}</span>
                      {r.status !== "open" && (
                        <span className={`ml-2 border px-2 py-0.5 text-[9px] uppercase tracking-widest ${r.status === "actioned" ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>{r.status}</span>
                      )}
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-muted-foreground">by <span className="text-foreground font-bold">{r.target_author_name || "unknown"}</span> — </span>
                      <span className="italic">"{r.target_preview || "(no preview)"}"</span>
                    </div>
                    {r.note && <div className="mt-1 text-xs text-muted-foreground">note: {r.note}</div>}
                    {link && (
                      <Link to={link} className="mt-2 inline-block text-[10px] uppercase tracking-widest text-primary hover:underline">Open →</Link>
                    )}
                  </div>
                  {r.status === "open" && (
                    <div className="flex gap-2 shrink-0">
                      <Button
                        data-testid={`report-dismiss-${r.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => dismiss(r.id)}
                        className="rounded-none"
                      >
                        <Check className="mr-1 h-3 w-3" />Dismiss
                      </Button>
                      <Button
                        data-testid={`report-action-${r.id}`}
                        size="sm"
                        onClick={() => action(r.id)}
                        className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        <Trash2 className="mr-1 h-3 w-3" />Delete
                      </Button>
                    </div>
                  )}
                </div>
                <div className="mt-2 mono text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
